import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Modal, Button, Spinner, ProgressBar } from 'react-bootstrap';
import Swal from 'sweetalert2';

const money = (n) => `₦${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const MAX_FILE_MB = 10;

export const CLAIM_STATUS_BADGE = {
  submitted: 'bg-secondary',
  received: 'bg-info',
  under_review: 'bg-warning',
  info_requested: 'bg-info',
  under_assessment: 'bg-primary',
  approved: 'bg-success',
  partially_approved: 'bg-success',
  rejected: 'bg-danger',
  settled: 'bg-dark',
};

const apiError = (error, fallback) => error?.response?.data?.message || fallback;

/**
 * Insurance claims: dashboard, report-a-claim wizard and claim details.
 * The claim form itself comes from the server (GET /agri-insurance/claims/form) so it always matches the backend.
 */
const AgriInsuranceClaims = ({ http, logout, active, startPolicy, onStarted }) => {
  const [view, setView] = useState('list'); // 'list' | 'new' | 'detail'
  const [detailId, setDetailId] = useState(null);
  const [schema, setSchema] = useState(null);
  const [schemaError, setSchemaError] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [prefillPolicy, setPrefillPolicy] = useState(null);

  const loadSchema = async () => {
    setSchemaError(null);
    try {
      const res = await http.get('/agri-insurance/claims/form');
      if (res.data.status === true) setSchema(res.data.data);
      else setSchemaError(res.data.message || 'Could not load the claim form');
    } catch (error) {
      if (error?.response?.status === 401) return logout();
      setSchemaError('Could not load the claim form, please try again');
    }
  };

  useEffect(() => {
    if (active && !schema) loadSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // "Report a claim" pressed on a policy elsewhere on the page
  useEffect(() => {
    if (startPolicy) {
      setPrefillPolicy(startPolicy);
      setView('new');
      onStarted && onStarted();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startPolicy]);

  if (!active) return null;

  const openDetail = (id) => {
    setDetailId(id);
    setView('detail');
  };

  if (view === 'new') {
    if (schemaError) return <RetryBox message={schemaError} onRetry={loadSchema} />;
    if (!schema) return <div className="text-center py-5"><Spinner animation="border" /></div>;
    return (
      <ReportClaim
        http={http}
        schema={schema}
        initialPolicy={prefillPolicy}
        onCancel={() => setView('list')}
        onDone={(id) => { setListVersion((v) => v + 1); openDetail(id); }}
      />
    );
  }

  if (view === 'detail') {
    return <ClaimDetail http={http} id={detailId} onBack={() => { setListVersion((v) => v + 1); setView('list'); }} />;
  }

  const startNew = () => {
    setPrefillPolicy(null);
    setView('new');
  };

  return <ClaimsDashboard http={http} logout={logout} version={listVersion} onNew={startNew} onOpen={openDetail} />;
};

const RetryBox = ({ message, onRetry }) => (
  <div className="text-center py-5">
    <p className="text-700">{message}</p>
    <button className="btn btn-primary" onClick={onRetry}>Try again</button>
  </div>
);

/* ---------------------------------------------------------------------
 * Dashboard
 * ------------------------------------------------------------------- */

const ClaimsDashboard = ({ http, logout, version, onNew, onOpen }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ summary: {}, claims: [] });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/agri-insurance/claims');
      if (res.data.status === true) setData(res.data.data);
      else setError(res.data.message || 'Could not load your claims');
    } catch (err) {
      if (err?.response?.status === 401) return logout();
      setError('Could not load your claims, please try again');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const s = data.summary || {};
  const tiles = [
    { label: 'Total Claims', value: s.total ?? 0, bg: 'bg-primary' },
    { label: 'Under Review', value: s.under_review ?? 0, bg: 'bg-info' },
    { label: 'Approved', value: s.approved ?? 0, bg: 'bg-success' },
    { label: 'Rejected', value: s.rejected ?? 0, bg: 'bg-danger' },
    { label: 'Total Claimed', value: money(s.total_claimed), bg: 'bg-secondary' },
    { label: 'Total Paid', value: money(s.total_paid), bg: 'bg-warning' },
  ];

  return (
    <>
      <div className="alert alert-primary bg-primary bg-opacity-10 border-0 mb-4">
        <h5 className="mb-1"><span className="fa fa-info-circle me-2"></span>Claims Reporting Guide</h5>
        <div className="fs--1">
          Use the policy number of an approved Agric Insurance policy. After you submit, the claims team reviews your documents,
          updates the claim status and records the approved amount and settlement details here.
        </div>
      </div>

      <div className="row g-3 mb-4">
        {tiles.map((t) => (
          <div key={t.label} className="col-6 col-md-4">
            <div className={`card ${t.bg} text-white h-100 border-0`}>
              <div className="card-body">
                <div className="fs-2 fw-bold">{loading ? '…' : t.value}</div>
                <div className="fw-semi-bold">{t.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="text-700 fs--1">Have your policy number and supporting documents (photos, reports, receipts) ready before you start.</div>
        <button className="btn btn-primary" onClick={onNew}><span className="fa fa-plus me-1"></span> Report New Claim</button>
      </div>

      <div className="card">
        <div className="card-header border-bottom">
          <h5 className="mb-0">Claim Activity</h5>
          <div className="text-500 fs--1">Track progress and open a claim to reply to the claims team or add documents.</div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : error ? (
            <RetryBox message={error} onRetry={load} />
          ) : (data.claims || []).length === 0 ? (
            <div className="text-center py-5">
              <span className="fa fa-folder-open fa-3x text-300 mb-3 d-block"></span>
              <h5>No Claims Yet</h5>
              <p className="text-700">You have not reported any claims yet. Use your approved policy number to submit your first claim.</p>
              <button className="btn btn-primary btn-lg" onClick={onNew}><span className="fa fa-plus me-1"></span> Start Your First Claim</button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover fs--1 mb-0">
                <thead>
                  <tr>
                    <th>Claim Ref</th>
                    <th>Policy</th>
                    <th>Claim Type</th>
                    <th>Date of Loss</th>
                    <th>Claimed</th>
                    <th>Approved</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.claims.map((c) => (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(c.id)}>
                      <td className="fw-bold">{c.reference}</td>
                      <td>{c.policy_number}<div className="text-500">{c.policy?.product}</div></td>
                      <td>{c.claim_type_label}</td>
                      <td>{c.loss_date}</td>
                      <td>{money(c.amount_claimed)}</td>
                      <td>{c.amount_approved != null ? money(c.amount_approved) : '—'}</td>
                      <td>
                        <span className={`badge ${CLAIM_STATUS_BADGE[c.status] || 'bg-secondary'}`}>{c.status_label}</span>
                        {c.status === 'info_requested' && <div className="text-info mt-1">Action needed</div>}
                      </td>
                      <td><button className="btn btn-sm btn-outline-primary">Open</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/* ---------------------------------------------------------------------
 * Report a claim
 * ------------------------------------------------------------------- */

const emptyForm = (prefill) => ({
  claimant: { ...(prefill?.claimant || {}) },
  incident: {},
  loss: {},
  settlement: { ...(prefill?.settlement || {}) },
  product: {},
});

const ReportClaim = ({ http, schema, initialPolicy, onCancel, onDone }) => {
  const policies = schema.policies || [];
  const [step, setStep] = useState(1);
  const [claimType, setClaimType] = useState(() => policies.find((p) => p.policy_number === initialPolicy)?.suggested_claim_type || '');
  const [policyNumber, setPolicyNumber] = useState(initialPolicy || '');
  const [policy, setPolicy] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const [form, setForm] = useState(() => emptyForm(schema.prefill));
  const [documents, setDocuments] = useState([]); // [{ file, category }]
  const [declared, setDeclared] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }
  const fileInput = useRef(null);

  const type = schema.claim_types.find((t) => t.key === claimType);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const verify = async () => {
    if (!claimType) return toast.error('Choose the type of claim.');
    if (!policyNumber.trim()) return toast.error('Enter your policy number.');
    setVerifying(true);
    try {
      const res = await http.post('/agri-insurance/claims/verify-policy', { policy_number: policyNumber.trim() });
      if (res.data.status === true) {
        setPolicy(res.data.data);
        setStep(2);
      } else {
        toast.error(res.data.message || 'Policy could not be verified');
      }
    } catch (error) {
      toast.error(apiError(error, 'Policy could not be verified'));
    } finally {
      setVerifying(false);
    }
  };

  const pickPolicy = (p) => {
    setPolicyNumber(p.policy_number);
    if (!claimType && p.suggested_claim_type) setClaimType(p.suggested_claim_type);
  };

  const setValue = (section, key, value) => setForm((f) => ({ ...f, [section]: { ...f[section], [key]: value } }));

  const allSections = type ? [...schema.sections, type.section] : schema.sections;

  const missing = () => {
    for (const section of allSections) {
      for (const field of section.fields) {
        if (!field.required || field.type === 'heading') continue;
        const v = form[section.key]?.[field.key];
        if (v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === '')) return field.label;
      }
    }
    return null;
  };

  const addFiles = (files) => {
    const list = Array.from(files || []);
    const tooBig = list.filter((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig.length) toast.error(`${tooBig.map((f) => f.name).join(', ')} is larger than ${MAX_FILE_MB}MB`);
    const defaultCategory = type?.document_types?.[0] || 'Other';
    setDocuments((d) => [...d, ...list.filter((f) => !tooBig.includes(f)).map((file) => ({ file, category: defaultCategory }))]);
    if (fileInput.current) fileInput.current.value = '';
  };

  const submit = async () => {
    const gap = missing();
    if (gap) return toast.error(`${gap} is required.`);
    const claimed = parseFloat(String(form.loss.amount_claimed || '').replace(/,/g, '')) || 0;
    if (policy?.sum_insured && claimed > policy.sum_insured) {
      return toast.error(`The amount claimed cannot be more than the sum insured of ${money(policy.sum_insured)}.`);
    }
    if (!declared) return toast.error('Please accept the declaration.');

    const confirm = await Swal.fire({
      title: 'Submit claim?',
      html: `You are claiming <b>${money(claimed)}</b> against policy <b>${policy.policy_number}</b>${documents.length ? ` with ${documents.length} document(s)` : ''}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Submit claim',
    });
    if (!confirm.isConfirmed) return;

    setSubmitting(true);
    let claim;
    try {
      const res = await http.post('/agri-insurance/claims', {
        policy_number: policy.policy_number,
        claim_type: claimType,
        form,
        declaration: true,
        channel: 'web',
      });
      if (res.data.status !== true) {
        setSubmitting(false);
        return toast.error(res.data.message || 'Could not submit your claim');
      }
      claim = res.data.data;
    } catch (error) {
      setSubmitting(false);
      return toast.error(apiError(error, 'Could not submit your claim'));
    }

    // The claim exists now; upload the evidence one file at a time so one failure does not lose the rest.
    const failed = [];
    setProgress({ done: 0, total: documents.length });
    for (let i = 0; i < documents.length; i++) {
      const fd = new FormData();
      fd.append('file', documents[i].file);
      fd.append('category', documents[i].category);
      try {
        const res = await http.post(`/agri-insurance/claims/${claim.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.data.status !== true) failed.push(documents[i].file.name);
      } catch (error) {
        failed.push(documents[i].file.name);
      }
      setProgress({ done: i + 1, total: documents.length });
    }
    setSubmitting(false);
    setProgress(null);

    if (failed.length) {
      await Swal.fire({
        title: `Claim ${claim.reference} submitted`,
        html: `These files did not upload: <b>${failed.join(', ')}</b>.<br/>You can add them from the claim page.`,
        icon: 'warning',
      });
    } else {
      toast.success(`Claim ${claim.reference} submitted.`);
    }
    onDone(claim.id);
  };

  return (
    <div className="card">
      <div className="card-body">
        <button className="btn btn-link px-0 mb-2" onClick={step === 1 ? onCancel : () => setStep(1)} disabled={submitting}>
          <span className="fa fa-arrow-left me-1"></span> {step === 1 ? 'Back to Claims' : 'Back'}
        </button>

        {step === 1 && (
          <div className="mx-auto" style={{ maxWidth: 640 }}>
            <h4 className="text-primary mb-1">Start a Claim</h4>
            <p className="text-700">Choose the type of claim and enter the policy number of your approved Agric Insurance policy.</p>
            <div className="alert alert-warning d-flex align-items-start fs--1 text-dark" role="alert">
              <span className="fa fa-info-circle me-2 mt-1"></span>
              <div>{schema.instructions}</div>
            </div>

            <label className="form-label fw-bold">Claim Type *</label>
            {schema.claim_types.map((t) => (
              <label key={t.key} className={`d-block border rounded p-3 mb-2 ${claimType === t.key ? 'border-primary bg-primary bg-opacity-10' : ''}`} style={{ cursor: 'pointer' }}>
                <input type="radio" className="form-check-input me-2" name="claimType" checked={claimType === t.key} onChange={() => setClaimType(t.key)} />
                <span className="fw-semi-bold">{t.label}</span>
                <div className="text-500 fs--1 ms-4">{t.description}</div>
              </label>
            ))}

            <label className="form-label fw-bold mt-3">Policy Number *</label>
            <input
              className="form-control"
              placeholder="e.g. AGI260918AB12C"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && verify()}
            />
            <div className="text-500 fs--1 mt-1">Enter the policy number for the insurance cover you want to claim against.</div>
            {policies.length > 0 && (
              <div className="mt-2">
                <span className="fs--1 text-700 me-2">Your active policies:</span>
                {policies.map((p) => (
                  <button key={p.id} type="button" className={`btn btn-sm me-2 mb-1 ${policyNumber === p.policy_number ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => pickPolicy(p)}>
                    {p.policy_number} · {p.product}
                  </button>
                ))}
              </div>
            )}

            <button className="btn btn-primary w-100 mt-4" disabled={verifying} onClick={verify}>
              {verifying ? <Spinner animation="border" size="sm" /> : <><span className="fa fa-arrow-right me-1"></span> Continue to Claim Form</>}
            </button>
          </div>
        )}

        {step === 2 && policy && type && (
          <>
            <h4 className="text-primary mb-1">{type.label} Claim</h4>
            <div className="alert alert-success fs--1">
              <b>Policy {policy.policy_number} verified.</b> {policy.provider} · {policy.product} · Sum insured {money(policy.sum_insured)}
              {policy.cover_start ? ` · Cover approved ${policy.cover_start}` : ''}
            </div>

            {allSections.map((section) => (
              <FormSection key={section.key} section={section} values={form[section.key]} onChange={(k, v) => setValue(section.key, k, v)} />
            ))}

            <div className="border-top pt-3 mb-4">
              <h5 className="text-primary">Supporting Documents & Evidence</h5>
              <p className="text-500 fs--1">
                Photos and videos should be taken at the location wherever possible. Up to {MAX_FILE_MB}MB per file (images, PDF, Word, Excel or video).
              </p>
              {documents.map((d, i) => (
                <div key={i} className="row g-2 align-items-center mb-2">
                  <div className="col-md-5 text-truncate"><span className="fa fa-paperclip me-2"></span>{d.file.name}</div>
                  <div className="col-md-5">
                    <select className="form-select form-select-sm" value={d.category} onChange={(e) => setDocuments((all) => all.map((x, j) => (j === i ? { ...x, category: e.target.value } : x)))}>
                      {type.document_types.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <button className="btn btn-sm btn-link text-danger" onClick={() => setDocuments((all) => all.filter((_, j) => j !== i))}>Remove</button>
                  </div>
                </div>
              ))}
              <input ref={fileInput} type="file" multiple className="d-none" accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv" onChange={(e) => addFiles(e.target.files)} />
              <button className="btn btn-outline-primary btn-sm" onClick={() => fileInput.current && fileInput.current.click()}>
                <span className="fa fa-upload me-1"></span> Add files
              </button>
            </div>

            <div className="border-top pt-3">
              <h5 className="text-primary">Declaration & Consent</h5>
              <p className="fs--1 text-700">{schema.declaration}</p>
              <div className="form-check mb-3">
                <input className="form-check-input" type="checkbox" id="claimDeclaration" checked={declared} onChange={(e) => setDeclared(e.target.checked)} />
                <label className="form-check-label" htmlFor="claimDeclaration">I have read and accept this declaration</label>
              </div>
              {progress && progress.total > 0 && (
                <div className="mb-2">
                  <div className="fs--1 mb-1">Uploading documents {progress.done} of {progress.total}</div>
                  <ProgressBar now={(progress.done / progress.total) * 100} />
                </div>
              )}
              <button className="btn btn-primary" disabled={submitting} onClick={submit}>
                {submitting ? <><Spinner animation="border" size="sm" /> Submitting...</> : 'Submit Claim'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const FormSection = ({ section, values, onChange }) => (
  <div className="border-top pt-3 mb-4">
    <h5 className="text-primary">{section.title}</h5>
    {section.note && <p className="text-500 fs--1">{section.note}</p>}
    <div className="row g-3">
      {section.fields.map((field, i) =>
        field.type === 'heading' ? (
          <div key={`h${i}`} className="col-12"><h6 className="mb-0 mt-2">{field.label}</h6></div>
        ) : (
          <Field key={field.key} field={field} value={values?.[field.key]} onChange={(v) => onChange(field.key, v)} />
        )
      )}
    </div>
  </div>
);

const Field = ({ field, value, onChange }) => {
  const id = `claim_${field.key}`;
  const wide = field.type === 'textarea' || field.type === 'checks';
  const label = (
    <label className="form-label" htmlFor={id}>
      {field.label}{field.required && <span className="text-danger"> *</span>}
    </label>
  );
  let input;
  switch (field.type) {
    case 'textarea':
      input = <textarea id={id} className="form-control" rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'select':
      input = (
        <select id={id} className="form-select" value={value || ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
      break;
    case 'checks': {
      const picked = Array.isArray(value) ? value : [];
      input = (
        <div>
          {field.options.map((o) => (
            <div key={o} className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="checkbox"
                id={`${id}_${o}`}
                checked={picked.includes(o)}
                onChange={(e) => onChange(e.target.checked ? [...picked, o] : picked.filter((x) => x !== o))}
              />
              <label className="form-check-label" htmlFor={`${id}_${o}`}>{o}</label>
            </div>
          ))}
        </div>
      );
      break;
    }
    case 'number':
      input = <input id={id} type="number" min="0" step="0.01" className="form-control" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'date':
      input = <input id={id} type="date" max={today()} className="form-control" value={value || ''} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'time':
      input = <input id={id} type="time" className="form-control" value={value || ''} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'datetime':
      input = <input id={id} type="datetime-local" className="form-control" value={value || ''} onChange={(e) => onChange(e.target.value)} />;
      break;
    default:
      input = <input id={id} type="text" maxLength={500} className="form-control" value={value || ''} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <div className={wide ? 'col-12' : 'col-md-6'}>
      {label}
      {input}
      {field.hint && <div className="text-500 fs--1 mt-1">{field.hint}</div>}
    </div>
  );
};

/* ---------------------------------------------------------------------
 * Claim details
 * ------------------------------------------------------------------- */

const ClaimDetail = ({ http, id, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claim, setClaim] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('');
  const fileInput = useRef(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get(`/agri-insurance/claims/${id}`);
      if (res.data.status === true) {
        setClaim(res.data.data);
        setCategory((c) => c || res.data.data.document_types?.[0] || 'Other');
      } else {
        setError(res.data.message || 'Could not load this claim');
      }
    } catch (err) {
      setError(apiError(err, 'Could not load this claim'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const res = await http.post(`/agri-insurance/claims/${id}/respond`, { message: reply.trim() });
      if (res.data.status === true) {
        toast.success(res.data.message || 'Reply sent');
        setClaim(res.data.data);
        setReplyOpen(false);
        setReply('');
      } else {
        toast.error(res.data.message || 'Could not send your reply');
      }
    } catch (err) {
      toast.error(apiError(err, 'Could not send your reply'));
    } finally {
      setSending(false);
    }
  };

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (fileInput.current) fileInput.current.value = '';
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${file.name} is larger than ${MAX_FILE_MB}MB`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      try {
        const res = await http.post(`/agri-insurance/claims/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.data.status === true) ok++;
        else toast.error(`${file.name}: ${res.data.message}`);
      } catch (err) {
        toast.error(`${file.name}: ${apiError(err, 'upload failed')}`);
      }
    }
    setUploading(false);
    if (ok) {
      toast.success(`${ok} document(s) uploaded`);
      load();
    }
  };

  if (loading && !claim) return <div className="text-center py-5"><Spinner animation="border" /></div>;
  if (error) return <RetryBox message={error} onRetry={load} />;
  if (!claim) return null;

  return (
    <>
      <button className="btn btn-link px-0 mb-2" onClick={onBack}><span className="fa fa-arrow-left me-1"></span> Back to Claims</button>

      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
            <div>
              <h4 className="mb-1">{claim.reference}</h4>
              <div className="text-700">{claim.claim_type_label} · Policy {claim.policy_number}{claim.policy?.product ? ` (${claim.policy.product})` : ''}</div>
            </div>
            <span className={`badge fs--1 ${CLAIM_STATUS_BADGE[claim.status] || 'bg-secondary'}`}>{claim.status_label}</span>
          </div>
          <div className="row g-3">
            <Stat label="Date of loss" value={claim.loss_date} />
            <Stat label="Amount claimed" value={money(claim.amount_claimed)} />
            <Stat label="Amount approved" value={claim.amount_approved != null ? money(claim.amount_approved) : '—'} />
            <Stat label="Submitted" value={claim.submitted_at} />
          </div>

          {claim.status === 'info_requested' && (
            <div className="p-3 rounded bg-info bg-opacity-10 mt-3">
              <div className="fw-bold mb-1">The claims team needs more information</div>
              <div className="mb-2">{claim.info_request_note}</div>
              <button className="btn btn-sm btn-primary" onClick={() => setReplyOpen(true)}>Reply</button>
              <span className="fs--1 text-700 ms-2">Add any requested documents below as well.</span>
            </div>
          )}
          {claim.customer_response && claim.status !== 'info_requested' && (
            <div className="fs--1 text-700 mt-3">Your last reply: {claim.customer_response}</div>
          )}
          {claim.remarks && (
            <div className={`mt-3 ${claim.status === 'rejected' ? 'text-danger' : 'text-success'}`}>
              {claim.status === 'rejected' ? 'Reason: ' : 'Note: '}{claim.remarks}
            </div>
          )}
          {claim.status === 'settled' && (
            <div className="text-success mt-2">Settled on {claim.settlement_date} · Reference {claim.settlement_reference}</div>
          )}
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="text-primary">Supporting Documents ({claim.documents.length})</h5>
          {claim.documents.length === 0 && <p className="text-500 fs--1">No documents uploaded yet.</p>}
          {claim.documents.map((d) => (
            <div key={d.id} className="d-flex justify-content-between border-bottom py-2 fs--1">
              <span><span className="fa fa-paperclip me-2"></span>{d.filename} <span className="text-500">· {d.category}</span></span>
              <span className="text-500">{d.uploaded_at}</span>
            </div>
          ))}
          {claim.can_upload && (
            <div className="row g-2 align-items-center mt-2">
              <div className="col-md-6">
                <select className="form-select form-select-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {(claim.document_types || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-md-6">
                <input ref={fileInput} type="file" multiple className="d-none" accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv" onChange={(e) => upload(e.target.files)} />
                <button className="btn btn-sm btn-outline-primary" disabled={uploading} onClick={() => fileInput.current && fileInput.current.click()}>
                  {uploading ? <Spinner animation="border" size="sm" /> : <><span className="fa fa-upload me-1"></span> Add documents</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {(claim.details || []).map((section) => (
        <div key={section.title} className="card mb-3">
          <div className="card-body">
            <h5 className="text-primary">{section.title}</h5>
            <table className="table table-sm fs--1 mb-0">
              <tbody>
                {section.rows.map((row, i) =>
                  row.heading ? (
                    <tr key={i}><th colSpan={2} className="pt-3">{row.label}</th></tr>
                  ) : (
                    <tr key={i}><td className="text-700" style={{ width: '45%' }}>{row.label}</td><td style={{ whiteSpace: 'pre-line' }}>{row.value}</td></tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <Modal show={replyOpen} onHide={() => setReplyOpen(false)}>
        <Modal.Header closeButton><Modal.Title>Reply to the claims team</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="fs--1 text-700">{claim.info_request_note}</p>
          <textarea className="form-control" rows={4} placeholder="Type your answer" value={reply} onChange={(e) => setReply(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReplyOpen(false)}>Cancel</Button>
          <Button variant="primary" disabled={sending || !reply.trim()} onClick={sendReply}>
            {sending ? <Spinner animation="border" size="sm" /> : 'Send'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const Stat = ({ label, value }) => (
  <div className="col-6 col-md-3">
    <div className="text-500 fs--1">{label}</div>
    <div className="fw-bold">{value || '—'}</div>
  </div>
);

export default AgriInsuranceClaims;
